/** Time zones, pure (the working hours round, 2026-11-01; moved out of `mail.ts`, where the notification hold had them): the local
 *  calendar parts of an instant in a zone, the instant of a local wall-clock time, and whether a name is a real zone. In plain words: a
 *  time zone is a place's rule for its clock ("Europe/Vienna"); the same moment reads 21:00 in Vienna and 15:00 in New York. */

/** the three-letter weekday names `Intl` writes, Sunday first (the order of `Date.getUTCDay()`) */
export const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

const zoneFormat = new Map<string, Intl.DateTimeFormat>();
function fmt(zone: string): Intl.DateTimeFormat {
  let f = zoneFormat.get(zone);
  if (!f) { f = new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short" }); zoneFormat.set(zone, f); }
  return f;
}

export type ZonedParts = { y: number; m: number; d: number; hh: number; mm: number; weekday: string };
/** the local calendar parts of an instant in a zone */
export function zonedParts(at: Date, zone: string): ZonedParts {
  const p: Record<string, string> = {};
  for (const part of fmt(zone).formatToParts(at)) p[part.type] = part.value;
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day), hh: Number(p.hour) % 24, mm: Number(p.minute), weekday: p.weekday ?? "" };
}

/** the instant of a local wall-clock time in a zone (two passes over the offset: enough on either side of a daylight-saving change); a
 *  minute count past the day's end (24:00) rolls into the next day as `Date.UTC` does */
export function zonedInstant(y: number, m: number, d: number, hh: number, mm: number, zone: string): Date {
  let guess = Date.UTC(y, m - 1, d, hh, mm);
  for (let i = 0; i < 2; i++) { const p = zonedParts(new Date(guess), zone); const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm); guess += Date.UTC(y, m - 1, d, hh, mm) - asUtc; }
  return new Date(guess);
}

/** whether `Intl` knows the zone (the notifier formats every time in a person's zone, and an unknown name would throw and stop the pass) */
export function isRealZone(zone: string): boolean {
  if (!zone || zone.length > 64) return false;
  try { new Intl.DateTimeFormat("en-US", { timeZone: zone }); return true; } catch { return false; }
}
